import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  TableSortLabel,
  Box,
  Typography
} from "@mui/material";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import { CategoryNode } from "./types/categoryTypes";
import CategoryRow from "./CategoryRow";
import {
  categoryTableContainerSx,
  categoryTableHeadRowSx,
  categoryTableHeadSortSx
} from "./categoryListStyles";

type Order = "asc" | "desc";

export type CategoryListTableProps = {
  categoryTree: CategoryNode[];
  filteredCount: number;
  selectedIds: string[];
  order: Order;
  orderBy: keyof import("./types/categoryTypes").Category;
  onSort: (property: keyof import("./types/categoryTypes").Category) => void;
  onSelectAll: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSelectOne: (id: string) => void;
  onEdit: (cat: import("./types/categoryTypes").Category) => void;
  onDelete: (cat: import("./types/categoryTypes").Category) => void;
  onView: (cat: import("./types/categoryTypes").Category) => void;
  isMobile: boolean;
};

const DESKTOP_COLUMNS = [
  { id: "name", label: "Category name" },
  { id: "productCount", label: "Products" },
  { id: "status", label: "Status" }
] as const;

const MOBILE_COLUMNS = [{ id: "name", label: "Category name" }] as const;

const CategoryListTable: React.FC<CategoryListTableProps> = ({
  categoryTree,
  filteredCount,
  selectedIds,
  order,
  orderBy,
  onSort,
  onSelectAll,
  onSelectOne,
  onEdit,
  onDelete,
  onView,
  isMobile
}) => {
  const { t } = useKivunovaTranslation();
  const columns = isMobile ? MOBILE_COLUMNS : DESKTOP_COLUMNS;

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        ...categoryTableContainerSx,
        overflowX: isMobile ? "hidden" : "auto"
      }}
    >
      <Table
        size={isMobile ? "small" : "medium"}
        stickyHeader={!isMobile}
        sx={{
          width: "100%",
          tableLayout: isMobile ? "fixed" : "auto"
        }}
      >
        <TableHead>
          <TableRow sx={categoryTableHeadRowSx}>
            <TableCell padding="checkbox" sx={{ width: isMobile ? 42 : 56 }}>
              <Checkbox
                color="primary"
                size={isMobile ? "small" : "medium"}
                checked={
                  filteredCount > 0 && selectedIds.length === filteredCount
                }
                indeterminate={
                  selectedIds.length > 0 && selectedIds.length < filteredCount
                }
                onChange={onSelectAll}
              />
            </TableCell>
            {columns.map((col) => (
              <TableCell
                key={col.id}
                sortDirection={orderBy === col.id ? order : false}
                sx={{
                  ...categoryTableHeadSortSx,
                  ...(isMobile && { width: "auto" })
                }}
              >
                <TableSortLabel
                  active={orderBy === col.id}
                  direction={orderBy === col.id ? order : "asc"}
                  onClick={() => onSort(col.id)}
                >
                  {t(`category-list-col-${col.id}`, col.label)}
                </TableSortLabel>
              </TableCell>
            ))}
            <TableCell
              align="center"
              sx={{ width: isMobile ? 48 : 72, whiteSpace: "nowrap" }}
            >
              {t("category-list-col-actions", "Actions")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {categoryTree.map((node) => (
            <CategoryRow
              key={node.id}
              category={node}
              selectedIds={selectedIds}
              onSelect={onSelectOne}
              onEdit={onEdit}
              onDelete={onDelete}
              onView={onView}
              isMobile={isMobile}
            />
          ))}
        </TableBody>
      </Table>

      <Box
        sx={{
          px: { xs: 1.5, sm: 2 },
          py: 1.25,
          borderTop: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.default"
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {t(
            "category-list-hierarchy-hint",
            "Click a parent category row to expand or collapse its subcategories."
          )}
        </Typography>
      </Box>
    </TableContainer>
  );
};

export default CategoryListTable;
